import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { AddOnItemName, SubscriptionPortalFlow } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { Plans } from "schemas/payments";

import { fetcherAuth } from "utils/api";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

interface CancelDialogProps {
  loading: boolean;
  open: boolean;
  onClose: () => void;
  warningText?: string;
  handleManagePlan: (flow?: SubscriptionPortalFlow) => void;
  plan: AddOnItemName | Plans;
}

enum CancelScreen {
  feedback = "feedback",
  feedback_success = "feedback_success",
  cancel = "cancel",
}

export default function CancelDialog({
  loading,
  open,
  onClose,
  warningText,
  handleManagePlan,
  plan,
}: CancelDialogProps) {
  const CANCEL_OPTIONS = [
    "I ran out of brands to pitch to",
    "I got no responses",
    "I'm confused on how to use Bento",
    "I switched services",
    "I'm pausing on my content for now",
    "Other",
  ];

  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { subscription } = useContext(SubscriptionContext);
  const { setErrorAlert } = useContext(AlertContext);

  const [screen, setScreen] = useState<CancelScreen>(CancelScreen.feedback);
  const [notes, setNotes] = useState<string>("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>("");

  const isPartnershipData = plan === AddOnItemName.partnership_data;
  const isCustomEmailData = plan === AddOnItemName.custom_email;
  const [amount, setAmount] = useState<string>("");

  const endDate =
    isPartnershipData && subscription?.partnershipData?.billingCycle
      ? subscription?.partnershipData?.billingCycle[1]
      : isCustomEmailData && subscription?.customEmailData?.billingCycle
        ? subscription.customEmailData.billingCycle[1]
        : subscription?.billingCycle[1];

  const fetchFeedback = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/paid`,
        "GET",
      );
      if (res.hasPaidUserFeedback) {
        setScreen(CancelScreen.cancel);
      }
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleSubmitFeedback = async () => {
    setSubmitLoading(true);
    try {
      const { amountInCents } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks`,
        "POST",
        {},
        {
          notes,
          plan,
          paid: true,
        },
      );
      // user submit feedback but decide to cancel.
      if (amountInCents === 0) {
        return;
      }
      const amount = Number(amountInCents / 100)?.toFixed(2);
      setAmount(amount);
      setScreen(CancelScreen.feedback_success);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleContinueToCancel = async () => {
    handleManagePlan(SubscriptionPortalFlow.subscription_cancel);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks`,
        "POST",
        {},
        {
          notes,
          plan,
          paid: false,
          cancellationReason: cancelReason,
        },
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const renderTextField = () => {
    return (
      <TextField
        multiline
        minRows={4}
        fullWidth
        sx={{ mt: 2 }}
        placeholder="Your feedback..."
        onChange={(e: CustomEvent) => setNotes(e.target.value)}
      />
    );
  };

  const renderScreen = () => {
    if (screen === CancelScreen.feedback) {
      return (
        <>
          We'd love a second chance! If you're willing to share feedback to let
          us know how we can improve, we'll give you{" "}
          <strong>50% off your next month </strong> to give us another shot.
          {renderTextField()}
        </>
      );
    } else if (screen === CancelScreen.feedback_success) {
      return (
        <>
          Feedback successfully submitted! We have credited{" "}
          <strong>${amount}</strong> to your credit balance. If you want to view
          your next invoice, you can click the button below.
          <Grid container justifyContent={"flex-end"} sx={{ mt: 2 }}>
            <Button
              disableElevation
              onClick={() => {
                trackEvent("User Feedback - See Next Invoice Button Clicked");
                handleManagePlan();
              }}
            >
              See Next Invoice
            </Button>
          </Grid>
        </>
      );
    } else {
      return (
        <>
          <Box sx={{ mb: 3 }}>
            If you plan to cancel, please select an option below.
            <RadioGroup
              name="radio-buttons-cancel"
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
              }}
            >
              {CANCEL_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
            {cancelReason === "Other" && renderTextField()}
          </Box>

          {warningText && (
            <Typography gutterBottom>
              <strong>Warning: </strong>
              {warningText}
            </Typography>
          )}

          <Box display="flex" gap={1} justifyContent="center" sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                trackEvent("User Feedback - Keep Subscription Button Clicked");
                onClose();
              }}
              disableElevation
            >
              Keep Subscription
            </Button>
            <LoadingButton
              loading={loading}
              color="secondary"
              onClick={handleContinueToCancel}
            >
              Continue to Cancel
            </LoadingButton>
          </Box>
          {endDate && (
            <Typography paragraph color="textSecondary" variant="caption">
              Your subscription will remain active until the end of your current
              billing period on{" "}
              <strong>
                {formatEpochTimestamp({
                  epochTimestamp: endDate,
                  addYear: true,
                })}
              </strong>
              .
            </Typography>
          )}
        </>
      );
    }
  };

  const getTitle = () => {
    if (screen === CancelScreen.feedback) {
      return "Something Not Working?";
    }
    if (screen === CancelScreen.feedback_success) {
      return "Feedback Submitted 🎉";
    }
    return "Before You Go";
  };

  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose}>
      <IconButton
        sx={{
          color: "primary.main",
          fontSize: 14,
          position: "absolute",
          right: 10,
          top: 10,
        }}
        onClick={onClose}
      >
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <DialogTitle>{getTitle()}</DialogTitle>

      <DialogContent>{renderScreen()}</DialogContent>

      {screen === CancelScreen.feedback && (
        <DialogActions>
          <Button
            color="secondary"
            onClick={() => {
              setScreen(CancelScreen.cancel);
              trackEvent("User Feedback - Continue to Cancel Button Clicked");
              setNotes("");
            }}
          >
            Continue to Cancel
          </Button>
          <LoadingButton
            disableElevation
            variant="contained"
            onClick={handleSubmitFeedback}
            loading={submitLoading}
          >
            Submit
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
}
